import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "overlay"]

  connect() {
    this._onKeydown = this._handleGlobalKeydown.bind(this)
    document.addEventListener('keydown', this._onKeydown)
    this._debounceTimer = null
  }

  disconnect() {
    document.removeEventListener('keydown', this._onKeydown)
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
  }

  _handleGlobalKeydown(event) {
    // "/" shortcut — only trigger if not in an input/textarea/contenteditable
    if (event.key === '/' && !this._isEditableElement(event.target)) {
      event.preventDefault()
      this.open()
      return
    }
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.open()
    }
    if (event.key === 'Escape' && this._isOpen) {
      this.close()
    }
  }

  open() {
    this._isOpen = true
    this.overlayTarget.classList.remove('hidden')
    this.inputTarget.value = ''
    this.resultsTarget.innerHTML = ''
    this.inputTarget.focus()
  }

  close() {
    this._isOpen = false
    this.overlayTarget.classList.add('hidden')
    this.inputTarget.value = ''
    this.resultsTarget.innerHTML = ''
  }

  search() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._doSearch(), 200)
  }

  async _doSearch() {
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.resultsTarget.innerHTML = '<p class="text-xs text-gray-400 p-3">Type at least 2 characters...</p>'
      return
    }

    try {
      // Search both tasks and tickets in parallel
      const [tasksResponse, ticketsResponse] = await Promise.all([
        fetch(`/command_centre/tasks/search?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json' }
        }),
        fetch(`/tickets_autocomplete.json?q=${encodeURIComponent(query)}`)
      ])

      const tasks = await tasksResponse.json()
      const tickets = await ticketsResponse.json()

      this._renderCombinedResults(tasks, tickets)
    } catch (error) {
      this.resultsTarget.innerHTML = '<p class="text-xs text-gray-400 p-3">Search failed</p>'
    }
  }

  _renderCombinedResults(tasks, tickets) {
    let html = ''

    // Tasks section
    if (tasks.length > 0) {
      html += '<div class="px-3 py-1.5 bg-gray-50 dark:bg-gray-900/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Command Centre Tasks</div>'
      html += tasks.map(task => this._renderTaskResult(task)).join('')
    }

    // Tickets section
    if (tickets.length > 0) {
      html += '<div class="px-3 py-1.5 bg-gray-50 dark:bg-gray-900/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tickets</div>'
      html += tickets.map(ticket => this._renderTicketResult(ticket)).join('')
    }

    if (tasks.length === 0 && tickets.length === 0) {
      html = '<p class="text-xs text-gray-400 p-3 text-center">No results found</p>'
    }

    this.resultsTarget.innerHTML = html
  }

  _renderTaskResult(task) {
    const quadrantColors = {
      do_now: 'bg-red-500', plan: 'bg-yellow-500', delegate: 'bg-blue-500',
      backlog: 'bg-gray-400', uncategorised: 'bg-purple-400'
    }
    const statusBadge = task.completed
      ? '<span class="text-[10px] bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded-full">Done</span>'
      : '<span class="text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">Open</span>'
    const quadrantDot = `<span class="w-2 h-2 rounded-full ${quadrantColors[task.quadrant] || 'bg-gray-400'} flex-shrink-0"></span>`
    const projectBadge = task.project_tag
      ? `<span class="text-[10px] bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded-full">${task.project_tag}</span>`
      : ''
    const assignee = task.assignee
      ? `<span class="text-[10px] text-gray-500 dark:text-gray-400"><i class="fas fa-user text-[8px] mr-0.5"></i>${task.assignee}</span>`
      : ''

    return `
      <button type="button"
              class="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
              data-action="click->command-search#selectTask"
              data-task-id="${task.id}"
              data-completed="${task.completed}">
        ${quadrantDot}
        <span class="text-sm text-gray-900 dark:text-white flex-1 truncate">${this._escapeHTML(task.title)}</span>
        ${statusBadge}
        ${projectBadge}
        ${assignee}
      </button>
    `
  }

  _renderTicketResult(ticket) {
    return `
      <a href="/tickets/${ticket.id}"
         class="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors block"
         data-action="click->command-search#selectTicket"
         data-ticket-id="${ticket.id}">
        <span class="w-6 h-6 rounded-md bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
          <i class="fas fa-ticket-alt text-indigo-600 dark:text-indigo-400 text-[10px]"></i>
        </span>
        <span class="text-xs font-semibold text-indigo-600 dark:text-indigo-400 flex-shrink-0">${ticket.uid}</span>
        <span class="text-sm text-gray-900 dark:text-white flex-1 truncate">${this._escapeHTML(ticket.summary)}</span>
      </a>
    `
  }

  selectTask(event) {
    const btn = event.currentTarget
    const taskId = btn.dataset.taskId
    const completed = btn.dataset.completed === 'true'
    this.close()
    if (completed) {
      const taskTitle = btn.querySelector('.truncate')?.textContent?.trim() || ''
      window.location.href = `/command_centre/history?q=${encodeURIComponent(taskTitle)}`
    } else {
      const taskCard = document.querySelector(`[data-task-id="${taskId}"]`)
      if (taskCard) {
        taskCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
        taskCard.classList.add('ring-2', 'ring-indigo-400')
        setTimeout(() => taskCard.classList.remove('ring-2', 'ring-indigo-400'), 2000)
      }
    }
  }

  selectTicket(event) {
    event.preventDefault()
    const ticketId = event.currentTarget.dataset.ticketId
    this.close()
    window.location.href = `/tickets/${ticketId}`
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  _escapeHTML(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  _isEditableElement(el) {
    const tag = el.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    if (el.isContentEditable) return true
    return false
  }
}
